/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';

import colors from '../../theme/colors';
import {
  Modal as ReactStrapModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps as ReactStrapModalProps
} from '../_reactstrap-shim';
import Button, { ButtonVariant } from '../Button/Button';
import IconButton from '../Button/IconButton';
import { CloseIcon, WarningIcon } from './icons';
import Text from './Text';

export enum ModalSize {
  small = 'sm',
  medium = 'md',
  large = 'lg',
  extra = 'xl'
}

export interface ModalProps extends ReactStrapModalProps {
  size?: ModalSize;
  containerTitle?: string;
  header?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmHandler?: () => void;
  cancelHandler?: () => void;
  showCancelButton?: boolean;
  confirmationInProgress?: boolean;
  children?: React.ReactNode | undefined;
  dangerModal?: boolean;
  headerCss?: SerializedStyles;
  bodyCss?: SerializedStyles;
  footerCss?: SerializedStyles;
  warningIcon?: boolean | false;
  backdrop?: boolean | 'static';
  displayCloseModalButton?: boolean;
}

const CustomClose = ({ onClick }: { onClick?: () => void }) => (
  <IconButton
    icon={CloseIcon}
    onClick={onClick}
    aria-label="Close"
    variant={ButtonVariant.Ghost}
    customCss={css`
      color: ${colors.black};
    `}
  />
);

const Modal = ({
  size = ModalSize.medium,
  containerTitle: title,
  header,
  confirmText = 'OK',
  cancelText = 'Cancel',
  confirmHandler,
  cancelHandler,
  showCancelButton = true,
  confirmationInProgress = false,
  children,
  dangerModal,
  headerCss,
  bodyCss,
  footerCss,
  warningIcon,
  backdrop = 'static',
  displayCloseModalButton = true,
  ...props
}: ModalProps): React.JSX.Element => {
  const cancelClick = confirmationInProgress ? () => {} : cancelHandler;
  const confirmClick = confirmationInProgress ? () => {} : confirmHandler;
  const showFooter = showCancelButton || confirmHandler;

  const [isConfirming, setIsConfirming] = React.useState(false);

  return (
    <ReactStrapModal {...props} isOpen toggle={cancelClick} size={size} backdrop={backdrop}>
      <ModalHeader close={displayCloseModalButton ? <CustomClose onClick={cancelClick} /> : null} css={headerCss}>
        {title && (
          <Text bold type="large">
            {warningIcon && (
              <WarningIcon
                css={css`
                  position: relative;
                  margin-right: 4px;
                `}
                color={colors.orange400}
              />
            )}
            {title}
          </Text>
        )}
        {header}
      </ModalHeader>
      <ModalBody css={bodyCss}>{children}</ModalBody>
      {showFooter && (
        <ModalFooter css={footerCss}>
          {showCancelButton && cancelHandler && (
            <Button variant={ButtonVariant.Outline} onClick={cancelClick}>
              {cancelText}
            </Button>
          )}
          {confirmHandler && (
            <Button
              onClick={async () => {
                setIsConfirming(true);
                await confirmClick?.();
                setIsConfirming(false);
              }}
              variant={dangerModal ? ButtonVariant.Destructive : ButtonVariant.Primary}
              loading={isConfirming || confirmationInProgress}
            >
              {confirmText}
            </Button>
          )}
        </ModalFooter>
      )}
    </ReactStrapModal>
  );
};

export default Modal;
